import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Building2, Loader2 } from 'lucide-react'

import {
  createDepartment,
  patchDepartment,
  type ProductionDepartment,
} from '@/api/studio-production'
import { resolveApiErrorMessage } from '@/features/studio/lib/resolveApiError'
import { ProtoBtn, ProtoInput, ProtoLabel, ProtoModal } from '@/features/studio/components/ui-kit'

export function DepartmentModal({
  open,
  onClose,
  productionId,
  mode,
  department,
  onSaved,
}: {
  open: boolean
  onClose: () => void
  productionId: string
  mode: 'create' | 'edit'
  department?: ProductionDepartment | null
  onSaved?: () => void
}) {
  const { t } = useTranslation()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    if (mode === 'edit' && department) {
      setName(department.name)
      setDescription(department.description ?? '')
    } else {
      setName('')
      setDescription('')
    }
    setError(null)
  }, [open, mode, department])

  async function handleSave() {
    const trimmedName = name.trim()
    if (!trimmedName) {
      setError(t('team.deptNameRequired'))
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const desc = description.trim()
      if (mode === 'create') {
        await createDepartment(productionId, {
          name: trimmedName,
          description: desc || undefined,
        })
      } else if (department) {
        await patchDepartment(productionId, department.id, {
          name: trimmedName,
          description: desc || undefined,
        })
      }
      onSaved?.()
      onClose()
    } catch (e) {
      setError(
        resolveApiErrorMessage(
          e,
          mode === 'create' ? 'team.deptCreateFailed' : 'team.deptPatchFailed',
        ),
      )
    } finally {
      setSubmitting(false)
    }
  }

  const title = mode === 'create' ? t('team.deptCreateTitle') : t('team.deptEditTitle')

  return (
    <ProtoModal
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          <ProtoBtn size="sm" variant="secondary" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </ProtoBtn>
          <ProtoBtn size="sm" onClick={() => void handleSave()} disabled={submitting}>
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Building2 className="h-4 w-4" />
            )}
            {submitting
              ? `${t('settings.save')}…`
              : mode === 'create'
                ? t('team.deptCreate')
                : t('settings.save')}
          </ProtoBtn>
        </>
      }
    >
      {error ? <p className="mb-3 text-xs text-rose-300">{t(error, { defaultValue: error })}</p> : null}
      <div className="space-y-3">
        <div>
          <ProtoLabel>{t('team.deptNameLabel')}</ProtoLabel>
          <ProtoInput
            id="dept-modal-name"
            placeholder={t('team.deptNamePlaceholder')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={submitting}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !submitting) void handleSave()
            }}
          />
        </div>
        <div>
          <ProtoLabel>{t('team.deptDescriptionLabel')}</ProtoLabel>
          <ProtoInput
            id="dept-modal-desc"
            placeholder={t('team.deptDescriptionPlaceholder')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={submitting}
          />
        </div>
      </div>
    </ProtoModal>
  )
}
